import React, { useEffect, useRef } from "react";
import { Form, InputNumber, Modal, Select, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { save } from "../../../../../features/save/saveSlice";

const VarietyAdd = ({
  isModalOpen,
  handleCancel,
  handleFetch,
  prodd,
  prodd2,
  productionDataId,
  dataSeason,
}) => {
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const { saving } = useSelector((state) => state.save);
  const dispatch = useDispatch();

  const onFinish = async (data) => {
    // averageProductionPerAcre and revenuePerAcre are derived server side
    const { seasonId, averageProductionPerAcre, revenuePerAcre, ...rest } =
      data;

    const season = dataSeason?.find((item) => item?.ssnId === seasonId);

    const payload = {
      ...rest,
      id: prodd?.id ? prodd?.id : null,
      productionDataId: productionDataId,
      variety: prodd?.id ? prodd?.variety : prodd2?.variety,
      season: seasonId
        ? { id: seasonId, name: season?.ssnSeason ?? null }
        : null,
    };

    const res = await dispatch(
      save({
        ...payload,
        url: "api/v1/production-varieties/save",
      }),
    );
    if (res?.payload?.success) {
      await toast.success(res?.payload?.message);
      await handleFetch();
      await form.resetFields();
      await handleCancel();
    } else {
      toast.error(res?.payload?.message);
    }
  };

  // Productivity (Kg/Acre)     = (Production (Tonnes) x 1000) / Acreage Under Irrigation (Acres)
  // Revenue per Acre (KES)     = Productivity x Farm Gate Price
  const acreage = Form.useWatch("acreage", form);
  const production = Form.useWatch("production", form);
  const farmGatePrice = Form.useWatch("farmGatePrice", form);

  useEffect(() => {
    const acres = Number(acreage);
    const tonnes = Number(production);
    const price = Number(farmGatePrice);
    const productivity = acres > 0 ? (tonnes * 1000) / acres : null;
    form.setFieldsValue({
      averageProductionPerAcre:
        productivity > 0 ? Number(productivity.toFixed(2)) : null,
      revenuePerAcre:
        productivity > 0 && price > 0
          ? Number((productivity * price).toFixed(2))
          : null,
    });
  }, [acreage, production, farmGatePrice, form]);

  useEffect(() => {
    if (prodd) {
      form.setFieldsValue({
        ...prodd,
        seasonId: prodd?.season?.id ?? null,
      });
    } else {
      form.resetFields();
    }
  }, [prodd, form]);

  return (
    <Modal
      title={`${
        prodd?.id
          ? "Update Season"
          : `Add a Season to ${prodd2?.variety?.name ?? "Variety"}`
      }`}
      open={isModalOpen}
      onOk={onFinish}
      onCancel={handleCancel}
      className="!w-[59.5rem]"
      maskClosable={false}
    >
      <Form
        layout="vertical"
        ref={formRef}
        name="control-ref"
        onFinish={onFinish}
        className=""
        style={{
          maxWidth: "100%",
        }}
        form={form}
      >
        <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
          <Form.Item
            name="seasonId"
            label={"Season"}
            rules={[
              {
                required: true,
                message: "Please select season",
              },
            ]}
          >
            <Select
              allowClear
              placeholder="Select season"
              style={{
                width: "100%",
              }}
              options={
                dataSeason?.length > 0 &&
                dataSeason?.map((item) => ({
                  value: item?.ssnId,
                  label: item?.ssnSeason,
                }))
              }
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label?.toLocaleLowerCase() ?? "").includes(
                  input?.toLocaleLowerCase(),
                )
              }
            />
          </Form.Item>{" "}
          <Form.Item name="production" label={"Production (Tonnes)"}>
            <InputNumber
              min={0}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
              placeholder="Enter value"
              className="input flex"
            />
          </Form.Item>
          <Form.Item
            name="acreage"
            label={"Acreage Under Irrigation (Acres)"}
          >
            <InputNumber
              min={0}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
              placeholder="Enter value"
              className="input flex"
            />
          </Form.Item>
          <Form.Item
            name="averageProductionPerAcre"
            label={"Productivity (Kg/Acre)"}
            extra={
              <span className="proj_desc">
                (Production (Tonnes) &times; 1,000) &divide; Acreage Under
                Irrigation (Acres)
              </span>
            }
          >
            <InputNumber
              disabled
              min={0}
              placeholder="Auto-calculated"
              className="input flex"
            />
          </Form.Item>
          <Form.Item
            name="farmGatePrice"
            label={"Farm Gate Price (KES per Kg)"}
          >
            <InputNumber
              min={0}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
              placeholder="Enter value"
              className="input flex"
            />
          </Form.Item>
          <Form.Item
            name="revenuePerAcre"
            label={"Revenue per Acre (KES)"}
            extra={
              <span className="proj_desc">
                Productivity (Kg/Acre) &times; Farm Gate Price (KES per Kg)
              </span>
            }
          >
            <InputNumber
              disabled
              min={0}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              placeholder="Auto-calculated"
              className="input flex"
            />
          </Form.Item>
          <Form.Item
            name="costPerAcre"
            label={"Cost of Production per Acre (KES)"}
          >
            <InputNumber
              min={0}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
              placeholder="Enter value"
              className="input flex"
            />
          </Form.Item>
          <Form.Item
            name="waterRequirement"
            label={"Water Requirement (m3/s)"}
          >
            <InputNumber
              min={0}
              parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
              placeholder="Enter value"
              className="input flex"
            />
          </Form.Item>
          <Form.Item
            name="availableFlow"
            label={"Available Water Flow (m3/s)"}
          >
            <InputNumber
              min={0}
              parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
              placeholder="Enter value"
              className="input flex"
            />
          </Form.Item>
        </div>

        <div className="flex justify-between mt-[48px] ">
          <div className="justify-start"></div>
          <div className="justify-end flex items-center mb-[58px] gap-x-5">
            <div className="w-[150px]">
              <button
                type="button"
                key="back"
                onClick={handleCancel}
                className="cstm-btn"
              >
                Cancel
              </button>
            </div>

            <div className="w-[200px]">
              <button
                disabled={saving}
                key="submit"
                type="submit"
                className="cstm-btn-2"
              >
                {saving ? <Spin /> : prodd?.id ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </div>
      </Form>
    </Modal>
  );
};
export default VarietyAdd;
